import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, NavigationEnd } from '@angular/router';

import { skip, Subscription, filter } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';

import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { PaginatorComponent } from '../../common/paginator/paginator.component';

import { UserAccountService } from '../../../services/user-account/user-account.service';
import { LeakSearchService } from '../../../services/leak-search/leak-search.service';
import { SearchHistoryService } from '../../../services/search-history/search-history.service';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import {
  getSearchVendorStatusLabel,
  isBackgroundRefreshPending,
  isVendorSyncComplete,
} from '../../../services/search-polling/osint-job-poll-response.util';
import { RestrictedAccessWarningDirective } from '../../../directives/restricted-access-warning.directive';
import { SearchServiceStatusService } from '../../../services/search-service-status/search-service-status.service';
import { CreditsLabelPipe } from '../../../pipes/credits-label.pipe';

@Component({
  selector: 'app-leak-email-search',
  standalone: true,
  imports: [CommonModule,
    FormsModule,
    HeaderComponent,
    PaginatorComponent,
    SidebarComponent,
    RestrictedAccessWarningDirective, CreditsLabelPipe],
  templateUrl: './leak-email-search.component.html',
  styleUrls: ['./leak-email-search.component.css'],
})
export class LeakEmailSearchComponent implements OnInit, OnDestroy {
  accessDetails: any;
  activeTab: string = 'email';
  availableCredits: number | null = null;
  creditsLoading = true;

  singleEmail: string = '';
  extraEmails: Array<{ value: string }> = [];
  readonly maxSearchRows = LeakSearchService.MAX_SEARCH_COUNT;
  isMultipleSearch: boolean = false;

  textAreaInput: string = '';

  allJobResultsSubscription?: Subscription;
  loadingJobIds: boolean = false;
  jobIds: string[] = [];

  private lastSearchTime: number = 0;
  private readonly SEARCH_THROTTLE_MS: number = 1000; // 1 second

  searchType: string = 'email';
  loadingRecentResults: boolean = false;
  recentResults: any = [];
  recentResultsPaged: any = [];
  ipAddress: string = '';
  location: string = '';

  recentResultsCount: number = 0;
  recentResultsPerPage = 5;
  recentResultsCurrentPage = 1;
  recentResultsHasPreviousPage: boolean = false;
  recentResultsHasNextPage: boolean = false;

  recentResultsSelectAll = { isSelected: false };
  isServiceDisabled: boolean = false;
  searchCreditCost: number | null = null;

  selectedFilterType: string = 'email';

  filterTypes: any = [
    { label: 'Email', value: 'email' },
    // { label: 'Status', value: 'status' },
    { label: 'Notes', value: 'notes' },
    { label: 'Date Added', value: 'date-added' },
  ];

  filterKey: string = '';
  filterValue: string = '';

  emailFilter: string = '';
  statusFilter: string = '';
  notesFilter: string = '';
  startDateFilter: string = '';
  endDateFilter: string = '';

  shouldExport: boolean = false;
  exportFormat: string = '';

  get recentResultsPagedStartAndEnd() {
    if (!this.recentResults?.length || !this.recentResultsCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.recentResultsCurrentPage - 1) * this.recentResultsPerPage + 1;
    const end = Math.min(
      start + this.recentResults.length - 1,
      this.recentResultsCount,
    );

    return { start, end };
  }

  get selectedRecentResults() {
    return this.recentResultsPaged
      .filter((recentResultPaged: any) => recentResultPaged.isSelected)
      .map((recentResultPaged: any) => recentResultPaged.leak_search_id);
  }

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private leakSearchService: LeakSearchService,
    private userAccountService: UserAccountService,
    private searchHistoryService: SearchHistoryService,
    private searchPollingService: SearchPollingService,
    private http: HttpClient,
    private searchServiceStatusService: SearchServiceStatusService,
  ) { }

  ngOnInit() {
    this.accessDetails = this.retrieveAccessDetails();

    // Check if Leak service is disabled
    this.searchServiceStatusService.searchServices$.subscribe((searchServices) => {
      if (searchServices) {
        this.isServiceDisabled = !searchServices.leak;
      }
    });

    this.searchServiceStatusService.creditCosts$.subscribe((cc) => {
      this.searchCreditCost = this.searchServiceStatusService.resolveCreditCost(
        cc,
        'leakSearchCost',
      );
    });

    this.getRecentResults();
    this.getAvailableCredits();

    // Subscribe to job results updates to keep status in sync
    this.subscribeToJobResultsUpdates();

    // Listen for navigation events to refresh results when returning to this page
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        if (event.url === '/leak-email-search') {
          this.getRecentResults();
        }
      });

    const autoSearchState = history.state;
    if (autoSearchState?.autoSearch && autoSearchState?.searchQuery) {
      this.singleEmail = autoSearchState.searchQuery;
      history.replaceState(
        { ...history.state, autoSearch: false, searchQuery: null },
        '',
      );
      setTimeout(() => {
        this.onSearchSingleEmailButtonClick();
      }, 100);
    }
  }

  /**
   * Subscribe to job results updates from SearchPollingService
   * This ensures status updates are reflected even when not actively polling
   */
  subscribeToJobResultsUpdates() {
    this.allJobResultsSubscription?.unsubscribe();

    this.allJobResultsSubscription = this.searchPollingService
      .getAllJobResults()
      .pipe(skip(1))
      .subscribe((map) => {
        // Update all results in recentResultsPaged that match jobs in the map
        this.recentResultsPaged.forEach((result: any) => {
          if (result.job_id) {
            const updatedStatus = map.get(result.job_id);
            if (updatedStatus) {
              result.done = updatedStatus.done || false;
              result.status = updatedStatus.status || 'pending';
              result.background_refresh_pending = Boolean(
                updatedStatus.background_refresh_pending,
              );
              result.vendor_sync_complete = isVendorSyncComplete(updatedStatus);
            }
          }
        });
      });
  }

  ngOnDestroy() {
    this.allJobResultsSubscription?.unsubscribe();
  }

  retrieveAccessDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    return userPortal?.accessDetails || null;
  }

  setTab(tab: string) {
    this.activeTab = tab;
    switch (tab) {
      case 'number':
        this.router.navigate(['/leak-number-search']);
        break;
      case 'email':
        this.router.navigate(['/leak-email-search']);
        break;
      case 'username':
        this.router.navigate(['/leak-username-search']);
        break;
      case 'password':
        this.router.navigate(['/leak-password-search']);
        break;
    }
  }

  getAvailableCredits() {
    this.creditsLoading = true;
    this.userAccountService.getUserProfile().subscribe({
      next: (response) => {
        // console.log('GET USER PROFILE RESPONSE: ', response);

        const subscription = response?.data?.subscriptions?.[0] || {};
        this.availableCredits = this.userAccountService.pickAvailableCredits(response);
        this.creditsLoading = false;
      },
      error: (error) => {
        console.error(error);
      
        this.availableCredits = 0;
        this.creditsLoading = false;
      },
      complete: () => { },
    });
  }

  getRecentResults() {
    this.loadingRecentResults = true;

    this.searchHistoryService
      .getLeakHistories(
        this.searchType,
        this.recentResultsCurrentPage,
        this.recentResultsPerPage,
        this.filterKey,
        this.filterValue,
        this.startDateFilter,
        this.endDateFilter,
      )
      .subscribe({
        next: (response) => {
          // console.log('GET LEAK HISTORIES RESPONSE: ', response);
          this.loadingRecentResults = false;

          this.recentResults = response?.data?.data_list;
          this.recentResultsPaged = this.addAdditionalKeys(this.recentResults);

          this.recentResultsCount = response?.data?.total_items;
          this.recentResultsHasPreviousPage = response?.data?.has_previous;
          this.recentResultsHasNextPage = response?.data?.has_next;

          // Check and refresh status for pending jobs
          this.refreshPendingJobStatuses();

          // console.log('RECENT RESULTS PAGED: ', this.recentResultsPaged);
        },
        error: (error) => {
          console.error(error);
          this.loadingRecentResults = false;
        },
      });
  }

  addAdditionalKeys(recentResults: any) {
    return recentResults.map((recentResult: any) => ({
      ...recentResult,
      isSelected: false,
    }));
  }

  onRecentResultPagedClick(event: Event, recentResultPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    this.updateRecentResultPaged(isChecked, recentResultPaged.leak_search_id);
  }

  updateRecentResultPaged(isChecked: boolean, searchId: string) {
    const recentResultsPagedToUpdate = this.recentResultsPaged.find(
      (recentResultPaged: any) => recentResultPaged.leak_search_id === searchId,
    );

    if (recentResultsPagedToUpdate) {
      recentResultsPagedToUpdate.isSelected = isChecked;
    }
  }

  onRecentResultsPageChange(page: number) {
    this.recentResultsCurrentPage = page;
    this.recentResultsSelectAll.isSelected = false;

    this.getRecentResults();
  }

  onDeleteButtonClick() {
    this.deleteRecentResults();
  }

  deleteRecentResults() {
    this.searchHistoryService
      .deleteLeakHistories(this.selectedRecentResults)
      .subscribe({
        next: (response) => {
          // console.log('DELETE LEAK HISTORIES RESPONSE: ', response);

          this.recentResultsSelectAll.isSelected = false;

          this.updateRecentResultsPaged();
          this.updateRecentResultsCurrentPage();

          this.getRecentResults();
        },
        error: (error) => {
          console.error(error);
        },
      });
  }

  updateRecentResultsPaged() {
    this.recentResultsPaged = this.recentResultsPaged.filter(
      (recentResultPaged: any) =>
        !this.selectedRecentResults.includes(recentResultPaged.leak_search_id),
    );
  }

  updateRecentResultsCurrentPage() {
    if (this.recentResultsPaged.length === 0) {
      if (this.recentResultsHasNextPage) {
      } else {
        if (this.recentResultsHasPreviousPage) {
          this.recentResultsCurrentPage--;
        }
      }
    }
  }

  onFilterTypeChange() {
    this.reset();
  }

  reset() {
    this.recentResultsCurrentPage = 1;

    this.filterKey = '';
    this.filterValue = '';

    this.emailFilter = '';
    this.statusFilter = '';
    this.notesFilter = '';
    this.startDateFilter = '';
    this.endDateFilter = '';
  }

  onMultipleSearchChange(): void {
    if (this.isMultipleSearch) {
      if (!this.textAreaInput.trim() && this.singleEmail.trim()) {
        this.textAreaInput = this.singleEmail.trim();
      }
      return;
    }
    if (!this.singleEmail.trim()) {
      const first = this.textAreaInput
        .split('\n')
        .map((value) => value.trim())
        .find((value) => value.length > 0);
      if (first) {
        this.singleEmail = first;
      }
    }
  }

  resetSearchInputs() {
    this.singleEmail = '';
    this.textAreaInput = '';
    this.extraEmails = [];
    this.isMultipleSearch = false;
  }

  onSearchSingleEmailButtonClick() {
    const values = this.collectEmailValues();
    if (!values.length) {
      this.toastr.error('Please enter an email');
      return;
    }
    if (values.length > this.maxSearchRows) {
      this.toastr.error('You can only search a maximum of 5 emails at once.');
      return;
    }
    if (!values.every((email) => this.validateEmail(email))) {
      this.toastr.error(
        values.length > 1 ? 'One or more emails are invalid' : 'Email is invalid',
      );
      return;
    }
    const totalCost = values.length * (this.searchCreditCost ?? 1);
    if (
      this.availableCredits != null &&
      this.availableCredits < totalCost
    ) {
      this.toastr.error(
        `Need ${totalCost} credits to search ${values.length} email(s). You have ${this.availableCredits}.`,
      );
      return;
    }
    if (this.isThrottled()) {
      return;
    }
    this.isMultipleSearch = values.length > 1;
    this.getJobIds(values);
  }

  get canAddSearchRow(): boolean {
    return this.extraEmails.length < this.maxSearchRows - 1;
  }

  addSearchRow(): void {
    if (!this.canAddSearchRow) {
      this.toastr.error('You can only search a maximum of 5 emails at once.');
      return;
    }
    this.extraEmails = [...this.extraEmails, { value: '' }];
    this.isMultipleSearch = this.extraEmails.length > 0;
  }

  removeSearchRow(index: number): void {
    this.extraEmails = this.extraEmails.filter((_, i) => i !== index);
    this.isMultipleSearch = this.extraEmails.length > 0;
  }

  private collectEmailValues(): string[] {
    const rawValues = this.isMultipleSearch
      ? this.textAreaInput.split('\n')
      : [this.singleEmail];
    return rawValues
      .map((value) => String(value || '').trim().replace(/\s+/g, ''))
      .filter((value) => value.length > 0);
  }

  validateEmail(email: string) {
    const trimmedEmail = email.trim().replace(/\s+/g, '');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return emailRegex.test(trimmedEmail);
  }

  private isThrottled(): boolean {
    const now = Date.now();
    const timeSinceLastSearch = now - this.lastSearchTime;

    return timeSinceLastSearch < this.SEARCH_THROTTLE_MS;
  }

  getJobIds(searchValues: string[]) {
    const { ip, location } = this.retrieveIPAndLocation();
    //if (!ip) return;

    this.loadingJobIds = true;
    this.lastSearchTime = Date.now();

    this.leakSearchService
      .getJobIds('email', searchValues, ip, location)
      .subscribe({
        next: (response) => {
          this.jobIds = LeakSearchService.extractJobIds(response?.data);
          const failedCount = response?.data?.failed_count ?? 0;

          if (this.jobIds.length === 0) {
            this.loadingJobIds = false;
            this.toastr.error('No searches were started');
            return;
          }

          if (failedCount > 0) {
            this.toastr.warning(
              `${this.jobIds.length} search(es) started. ${failedCount} could not be started.`,
            );
          }

          this.searchPollingService.startPolling(this.jobIds, (jobIds) =>
            this.leakSearchService.getSearchResults('email', jobIds),
          );
          this.searchPollingService.ensureBackgroundPolling(
            (this.jobIds || []).map((jobId: string, index: number) => ({
              job_id: jobId,
              searchType: 'leak-email',
              label: String(searchValues?.[index] ?? jobId),
            })),
            3 * 1000,
          );
          this.getAllJobResults();

          this.reset();
          this.resetSearchInputs();
          this.getRecentResults();
          this.getAvailableCredits();

          this.loadingJobIds = false;
        },
        error: (error) => {
          console.error(error);
          this.loadingJobIds = false;

          let errorMessage = 'An error occurred while processing your request';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Search Failed');
        },
      });
  }

  /**
   * Refresh status for pending jobs using the centralized SearchPollingService
   */
  refreshPendingJobStatuses() {
    this.recentResultsPaged.forEach((result: any) => {
      if (result.job_id) {
        const cachedStatus = this.searchPollingService.getCachedJobStatus(
          result.job_id,
        );
        if (cachedStatus) {
          result.done = cachedStatus.done || false;
          result.status = cachedStatus.status || 'pending';
          result.background_refresh_pending = Boolean(
            cachedStatus.background_refresh_pending,
          );
          result.vendor_sync_complete = isVendorSyncComplete(cachedStatus);
        }
      }
    });

    const pendingJobIds = this.recentResultsPaged
      .filter((result: any) => result.job_id && !isVendorSyncComplete(result))
      .map((result: any) => result.job_id)
      .filter((jobId: string) => !!jobId);

    if (pendingJobIds.length > 0) {
      this.searchPollingService
        .checkJobStatus(pendingJobIds, (jobIds) =>
          this.leakSearchService.getSearchResults(this.searchType, jobIds),
        )
        .subscribe({
          next: (jobResultsMap) => {
            this.recentResultsPaged.forEach((result: any) => {
              if (result.job_id) {
                const updatedStatus = jobResultsMap.get(result.job_id);
                if (updatedStatus) {
                  result.done = updatedStatus.done || false;
                  result.status = updatedStatus.status || 'pending';
                  result.background_refresh_pending = Boolean(
                    updatedStatus.background_refresh_pending,
                  );
                  result.vendor_sync_complete =
                    isVendorSyncComplete(updatedStatus);
                }
              }
            });
          },
          error: (error) => {
            console.warn('Failed to refresh job statuses:', error);
          },
        });
    }
  }

  isRecentRowComplete(row: any): boolean {
    return isVendorSyncComplete(row);
  }

  isRecentRowRefreshing(row: any): boolean {
    return Boolean(row?.done) && isBackgroundRefreshPending(row);
  }

  getRecentRowStatusLabel(row: any): string {
    return getSearchVendorStatusLabel(row);
  }

  getAllJobResults() {
    // No-op: subscription is handled in subscribeToJobResultsUpdates()
  }

  updateAllJobResults(results: any[]) {
    results.forEach((result) => {
      const index = this.recentResultsPaged.findIndex(
        (item: any) => item.job_id === result.job_id,
      );

      if (index !== -1 && result !== undefined) {
        // Update the done status from the polling result
        this.recentResultsPaged[index].done = result.done || false;
      }
    });
  }

  extractJobIds(jobs: any[]) {
    // Handle new API format: data is an array of job ID strings
    if (!jobs || !Array.isArray(jobs)) {
      return [];
    }

    // If jobs is an array of strings (new format), return as is
    if (jobs.length > 0 && typeof jobs[0] === 'string') {
      return jobs;
    }

    // Fallback for old format: array of objects with job_id and done properties
    return jobs.filter((job) => job.done === false).map((job) => job.job_id);
  }

  goToSearchResultPage(searchResult: any) {
    // console.log('SEARCH RESULT: ', searchResult);

    this.router.navigate(
      [`/leak-email-search/search-result/${searchResult.job_id}`],
      {
        state: { searchResult },
      },
    );
  }

  onRecentResultsSelectAllClick() {
    this.recentResultsSelectAll.isSelected =
      !this.recentResultsSelectAll.isSelected;

    this.recentResultsSelectAll.isSelected
      ? this.recentResultsPagedSelectAll()
      : this.recentResultsPagedDeselectAll();
  }

  recentResultsPagedSelectAll() {
    this.recentResultsPaged = this.recentResultsPaged.map(
      (recentResultPaged: any) => ({
        ...recentResultPaged,
        isSelected: true,
      }),
    );
  }

  recentResultsPagedDeselectAll() {
    this.recentResultsPaged = this.recentResultsPaged.map(
      (recentResultPaged: any) => ({
        ...recentResultPaged,
        isSelected: false,
      }),
    );
  }

  onEmailFilterChange(email: string) {
    const trimmedEmail = email.trim();

    if (trimmedEmail === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    } else if (trimmedEmail.length === 1) {
      return;
    } else {
      this.filterKey = 'query';
      this.filterValue = trimmedEmail;

      this.getRecentResults();
    }
  }

  onStatusFilterChange(status: string) {
    this.statusFilter = status;

    if (this.statusFilter === 'all') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.statusFilter === 'success') {
      this.filterKey = 'status';
      this.filterValue = 'success';

      this.getRecentResults();
    }

    if (this.statusFilter === 'pending') {
      this.filterKey = 'status';
      this.filterValue = 'pending';

      this.getRecentResults();
    }
  }

  onNotesFilterChange(notes: string) {
    const trimmedNotes = notes.trim();

    if (trimmedNotes === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    } else if (trimmedNotes.length === 1) {
      return;
    } else {
      this.filterKey = 'notes';
      this.filterValue = trimmedNotes;

      this.getRecentResults();
    }
  }

  onStartDateFilterChange(startDate: string) {
    this.startDateFilter = startDate;

    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.getRecentResults();
    }
  }

  onEndDateFilterChange(endDate: string) {
    this.endDateFilter = endDate;

    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.getRecentResults();
    }
  }

  exportRecentResults() {
    this.searchHistoryService
      .exportLeakHistories(
        this.searchType,
        this.recentResultsCurrentPage,
        this.recentResultsPerPage,
        this.filterKey,
        this.filterValue,
        this.startDateFilter,
        this.endDateFilter,
        this.shouldExport,
        this.exportFormat,
      )
      .subscribe({
        next: (response) => {
          // console.log('EXPORT LEAK HISTORIES RESPONSE: ', response);

          this.shouldExport = false;
          this.exportFormat = '';

          this.saveExportedFile(response?.data);
        },
        error: (error) => {
          console.error(error);
        },
      });
  }

  saveExportedFile(fileInfo: any) {
    const { fileName, mimeType, base64 } = fileInfo;

    const linkSource = `data:${mimeType};base64,${base64}`;
    const saveLink = document.createElement('a');

    saveLink.href = linkSource;
    saveLink.download = fileName;
    saveLink.click();
  }

  onExportExcelButtonClick() {
    this.shouldExport = true;
    this.exportFormat = 'xlsx';

    this.exportRecentResults();
  }

  onExportCsvButtonClick() {
    this.shouldExport = true;
    this.exportFormat = 'csv';

    this.exportRecentResults();
  }

  onExportPdfButtonClick() {
    this.shouldExport = true;
    this.exportFormat = 'pdf';

    this.exportRecentResults();
  }
  private retrieveIPAndLocation(): { ip: string; location: string } {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    const ipRaw = userPortal?.ipDetails?.ip || '';
    const location = userPortal?.ipDetails?.city || '';

    const ip = ipRaw.trim();
    const isValidIPv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(ip);
    console.log('🧪 Retrieved IP:', ip, 'Valid:', isValidIPv4);

    return {
      ip: isValidIPv4 ? ip : '',
      location,
    };
  }

  onBuyCreditsButtonClick() {
    this.router.navigate(['/plan-pricing']);
  }

  /** Truncate text to max words; if longer, append " ....." */
  truncateToWords(text: string | null | undefined, maxWords: number = 20): string {
    if (text == null || text === '') return '';
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + ' .....';
  }
}
